import React from 'react';

import { useConfiguration } from '../../context/configurationContext';

import { useIntl } from '../../util/reactIntl';
import * as validators from '../../util/validators';
import { getCountryOptions } from '../../util/shipping';

import { FieldLocationAutocompleteInput } from '../LocationAutocompleteInput/LocationAutocompleteInput';
import FieldPhoneNumberInput from '../FieldPhoneNumberInput/FieldPhoneNumberInput';
import FieldSelect from '../FieldSelect/FieldSelect';
import FieldTextInput from '../FieldTextInput/FieldTextInput';

import css from './ShippingAddressForm.module.css';

const identity = value => value;
const E164_PHONE_RE = /^\+[1-9]\d{7,14}$/;

const phoneFormatValid = message => value => {
  return !value || E164_PHONE_RE.test(value) ? undefined : message;
};

const getComponentText = (component, useShortText = false) => {
  if (!component) {
    return '';
  }

  const value = useShortText
    ? component.shortText || component.short_name || component.longText || component.long_name
    : component.longText || component.long_name || component.shortText || component.short_name;
  return `${value || ''}`.trim();
};

const findGoogleComponent = (components, type) => {
  return (components || []).find(component => component.types?.includes(type));
};

const parseGoogleAddress = place => {
  const components = place?.addressComponents || [];
  const streetNo = getComponentText(findGoogleComponent(components, 'street_number'));
  const street1 =
    getComponentText(findGoogleComponent(components, 'route')) || place?.address || '';
  const city =
    getComponentText(findGoogleComponent(components, 'locality')) ||
    getComponentText(findGoogleComponent(components, 'postal_town')) ||
    getComponentText(findGoogleComponent(components, 'administrative_area_level_2'));
  const state = getComponentText(findGoogleComponent(components, 'administrative_area_level_1'));
  const zip = getComponentText(findGoogleComponent(components, 'postal_code'));
  const country = getComponentText(findGoogleComponent(components, 'country'), true).toUpperCase();

  return { street1, streetNo, city, state, zip, country };
};

const getMapboxContext = (context, idPrefix) => {
  return (context || []).find(item => item.id?.startsWith(idPrefix));
};

const parseMapboxAddress = place => {
  const mapboxPlace = place?.mapboxPlace || {};
  const context = mapboxPlace.context || [];
  const countryContext = getMapboxContext(context, 'country');
  const stateContext = getMapboxContext(context, 'region');
  const cityContext =
    getMapboxContext(context, 'place') ||
    getMapboxContext(context, 'locality') ||
    getMapboxContext(context, 'district');
  const postcodeContext = getMapboxContext(context, 'postcode');
  const countryCode = countryContext?.short_code || countryContext?.properties?.short_code || '';

  return {
    street1: mapboxPlace.text || '',
    streetNo: mapboxPlace.address || '',
    city: cityContext?.text || '',
    state: stateContext?.text || stateContext?.short_code || '',
    zip: postcodeContext?.text || '',
    country: countryCode.toUpperCase(),
  };
};

const parseSelectedAddress = selectedPlace => {
  const parsedAddress = selectedPlace?.addressComponents
    ? parseGoogleAddress(selectedPlace)
    : selectedPlace?.mapboxPlace
    ? parseMapboxAddress(selectedPlace)
    : {};

  return Object.fromEntries(
    Object.entries(parsedAddress).filter(([, value]) => !!`${value || ''}`.trim())
  );
};

const getStreetSearchValue = values => {
  const selectedPlace = values?.street1Autocomplete?.selectedPlace;
  const street1 = `${values?.street1 || ''}`.trim();

  return {
    ...(values?.street1Autocomplete || {}),
    search: values?.street1Autocomplete?.search || street1 || selectedPlace?.address || '',
  };
};

const AddressAutocompleteSync = props => {
  const { form } = props;

  React.useEffect(() => {
    let previousSearch;
    let previousSelectedPlace;

    return form.subscribe(
      ({ values }) => {
        const autocompleteValue = values?.street1Autocomplete || {};
        const search = autocompleteValue.search;
        const selectedPlace = autocompleteValue.selectedPlace;
        const selectedPlaceChanged = selectedPlace && selectedPlace !== previousSelectedPlace;
        const searchChanged = search !== previousSearch;

        previousSearch = search;
        previousSelectedPlace = selectedPlace;

        if (selectedPlaceChanged) {
          const parsedAddress = parseSelectedAddress(selectedPlace);
          Object.entries(parsedAddress).forEach(([field, value]) => {
            form.change(field, value);
          });
          if (parsedAddress.street1 && parsedAddress.street1 !== search) {
            form.change('street1Autocomplete', {
              ...autocompleteValue,
              search: parsedAddress.street1,
              predictions: [],
              selectedPlace,
            });
          }
          return;
        }

        if (searchChanged && typeof search === 'string' && search !== values.street1) {
          form.change('street1', search);
        }
      },
      { values: true }
    );
  }, [form]);

  return null;
};

/**
 * Shipping address form fields shared between account settings and checkout.
 *
 * @param {Object} props
 * @returns {JSX.Element}
 */
const ShippingAddressFields = props => {
  const { allowedCountryCodes, form, formId = 'ShippingAddressForm' } = props;
  const config = useConfiguration();
  const intl = useIntl();

  const locale = config.localization.locale;
  const countryOptions = getCountryOptions(locale, allowedCountryCodes);
  const requiredMessage = intl.formatMessage({ id: 'ShippingAddressForm.required' });
  const phoneInvalidMessage = intl.formatMessage({ id: 'ShippingAddressForm.phoneInvalid' });

  return (
    <>
      <AddressAutocompleteSync form={form} />

      <div className={css.formRow}>
        <FieldTextInput
          id={`${formId}.name`}
          name="name"
          className={css.field}
          type="text"
          label={intl.formatMessage({ id: 'ShippingAddressForm.nameLabel' })}
          placeholder={intl.formatMessage({ id: 'ShippingAddressForm.namePlaceholder' })}
          validate={validators.required(requiredMessage)}
          onUnmount={() => form.change('name', undefined)}
        />
        <FieldPhoneNumberInput
          id={`${formId}.phone`}
          name="phone"
          className={css.field}
          label={intl.formatMessage({ id: 'ShippingAddressForm.phoneLabel' })}
          placeholder={intl.formatMessage({ id: 'ShippingAddressForm.phonePlaceholder' })}
          validate={validators.composeValidators(
            validators.required(requiredMessage),
            phoneFormatValid(phoneInvalidMessage)
          )}
          onUnmount={() => form.change('phone', undefined)}
        />
      </div>

      <div className={css.formRow}>
        <FieldLocationAutocompleteInput
          rootClassName={css.autocompleteRoot}
          inputClassName={css.autocompleteInput}
          iconClassName={css.autocompleteIcon}
          predictionsClassName={css.predictionsRoot}
          validClassName={css.validLocation}
          name="street1Autocomplete"
          id={`${formId}.street1`}
          label={intl.formatMessage({ id: 'ShippingAddressForm.street1Label' })}
          placeholder={intl.formatMessage({ id: 'ShippingAddressForm.street1Placeholder' })}
          useDefaultPredictions={false}
          useDarkText
          format={identity}
          valueFromForm={getStreetSearchValue(form.getState().values)}
          validate={value => {
            const currentStreet1 = form.getState().values.street1;
            return validators.required(requiredMessage)(currentStreet1 || value?.search);
          }}
        />
        <FieldTextInput
          id={`${formId}.streetNo`}
          name="streetNo"
          className={css.field}
          type="text"
          label={intl.formatMessage({ id: 'ShippingAddressForm.streetNoLabel' })}
          placeholder={intl.formatMessage({ id: 'ShippingAddressForm.streetNoPlaceholder' })}
          onUnmount={() => form.change('streetNo', undefined)}
        />
      </div>

      <div className={css.formRow}>
        <FieldTextInput
          id={`${formId}.line2`}
          name="line2"
          className={css.field}
          type="text"
          label={intl.formatMessage({ id: 'ShippingAddressForm.line2Label' })}
          placeholder={intl.formatMessage({ id: 'ShippingAddressForm.line2Placeholder' })}
          onUnmount={() => form.change('line2', undefined)}
        />
        <FieldTextInput
          id={`${formId}.city`}
          name="city"
          className={css.field}
          type="text"
          label={intl.formatMessage({ id: 'ShippingAddressForm.cityLabel' })}
          placeholder={intl.formatMessage({ id: 'ShippingAddressForm.cityPlaceholder' })}
          validate={validators.required(requiredMessage)}
          onUnmount={() => form.change('city', undefined)}
        />
      </div>

      <div className={css.formRow}>
        <FieldTextInput
          id={`${formId}.state`}
          name="state"
          className={css.field}
          type="text"
          label={intl.formatMessage({ id: 'ShippingAddressForm.stateLabel' })}
          placeholder={intl.formatMessage({ id: 'ShippingAddressForm.statePlaceholder' })}
          validate={validators.required(requiredMessage)}
          onUnmount={() => form.change('state', undefined)}
        />
        <FieldTextInput
          id={`${formId}.zip`}
          name="zip"
          className={css.field}
          type="text"
          label={intl.formatMessage({ id: 'ShippingAddressForm.zipLabel' })}
          placeholder={intl.formatMessage({ id: 'ShippingAddressForm.zipPlaceholder' })}
          validate={validators.required(requiredMessage)}
          onUnmount={() => form.change('zip', undefined)}
        />
      </div>

      <div className={css.formRow}>
        <FieldSelect
          id={`${formId}.country`}
          name="country"
          className={css.field}
          selectClassName={css.select}
          label={intl.formatMessage({ id: 'ShippingAddressForm.countryLabel' })}
          validate={validators.required(requiredMessage)}
        >
          <option disabled value="">
            {intl.formatMessage({ id: 'ShippingAddressForm.countryPlaceholder' })}
          </option>
          {countryOptions.map(country => (
            <option key={country.code} value={country.code}>
              {country.name}
            </option>
          ))}
        </FieldSelect>
      </div>
    </>
  );
};

export default ShippingAddressFields;
